import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Admin.css';

const CommunicationManagement = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emails, setEmails] = useState([]);
  const [emailsLoaded, setEmailsLoaded] = useState(false);
  
  // Preferred students filter state
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [timePeriod, setTimePeriod] = useState('6m');
  const [minVisits, setMinVisits] = useState(2);
  const [showPreferredFilter, setShowPreferredFilter] = useState(false);
  
  // Email composer state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfError, setPdfError] = useState('');
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Rich text editor ref
  const editorRef = useRef(null);
  
  // Format text functions
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };
  
  // Handle content change from contentEditable
  const handleContentChange = () => {
    if (editorRef.current) {
      setEmailContent(editorRef.current.innerHTML);
    }
  };

  // Fetch all client emails
  const fetchClientEmails = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get('communication/admin/communication/emails');
      
      if (response.data.success) {
        setEmails(response.data.emails);
        setEmailsLoaded(true);
      } else {
        setError('Failed to fetch client emails');
      }
    } catch (err) {
      console.error('Error fetching client emails:', err);
      
      // Check for permission denied error (403)
      if (err.response && err.response.status === 403) {
        setError(t('communication_access_denied') || 'You do not have permission to access communication features');
      } else {
        setError(t('error_fetching_emails') || 'Failed to fetch client emails. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch instructors for preferred students filter
  const fetchInstructors = async () => {
    try {
      const response = await axios.get('communication/admin/communication/instructors');
      if (response.data.success) {
        setInstructors(response.data.instructors);
      }
    } catch (err) {
      console.error('Error fetching instructors:', err);
    }
  };
  
  // Fetch classes for selected instructor
  const fetchClassesByInstructor = async (instructorId) => {
    try {
      const response = await axios.get(`communication/admin/communication/classes-by-instructor?instructor_id=${instructorId}`);
      if (response.data.success) {
        setAvailableClasses(response.data.classes);
        setSelectedClass('');
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError('Nepodarilo sa načítať triedy.');
    }
  };
  
  // Handle instructor selection
  const handleInstructorChange = (e) => {
    const instructorId = e.target.value;
    setSelectedInstructor(instructorId);
    if (instructorId) {
      fetchClassesByInstructor(instructorId);
    } else {
      setAvailableClasses([]);
      setSelectedClass('');
    }
  };
  
  // Find preferred students
  const handleFindPreferredStudents = async () => {
    if (!selectedInstructor || !selectedClass) {
      setError('Prosím vyberte inštruktora a triedu.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('communication/admin/communication/preferred-students', {
        instructorId: selectedInstructor,
        className: selectedClass,
        timePeriod: timePeriod,
        minVisits: minVisits
      });
      
      if (response.data.success) {
        // Auto-select the preferred students in the email list
        const preferredEmails = response.data.students.map(s => s.email);
        setSelectedRecipients(prev => {
          // Merge with existing selections (deduplicate)
          return [...new Set([...prev, ...preferredEmails])];
        });
        
        setSuccess(`Našlo sa ${response.data.count} preferovaných študentov. Boli automaticky vybraní.`);
      } else {
        setError('Nepodarilo sa nájsť preferovaných študentov.');
      }
    } catch (err) {
      console.error('Error finding preferred students:', err);
      setError('Nepodarilo sa nájsť preferovaných študentov. Skúste prosím znova.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle recipient checkbox toggle
  const handleRecipientToggle = (email) => {
    setSelectedRecipients(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };
  
  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (selectedRecipients.length === emails.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(emails.map(client => client.email));
    }
  };
  
  // Handle PDF file selection
  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    setPdfError('');
    
    if (file) {
      // Check file type
      if (file.type !== 'application/pdf') {
        setPdfError('Prosím vyberte PDF súbor.');
        setPdfFile(null);
        e.target.value = '';
        return;
      }
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setPdfError('Súbor je príliš veľký. Maximálna veľkosť je 2MB.');
        setPdfFile(null);
        e.target.value = '';
        return;
      }
      
      setPdfFile(file);
    }
  };
  
  // Handle preview
  const handlePreview = async () => {
    setError('');
    
    // Validate inputs
    if (!emailSubject.trim()) {
      setError('Prosím zadajte predmet emailu.');
      return;
    }
    
    if (!emailContent.trim()) {
      setError('Prosím zadajte obsah emailu.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post('communication/admin/communication/preview', {
        subject: emailSubject,
        content: emailContent,
        attachment_name: pdfFile ? pdfFile.name : null
      });
      
      if (response.data.success) {
        setPreviewHtml(response.data.preview_html);
        setShowPreview(true);
      } else {
        setError('Nepodarilo sa vytvoriť náhľad emailu.');
      }
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Nepodarilo sa vytvoriť náhľad emailu. Skúste prosím znova.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle send test
  const handleSendTest = async () => {
    setError('');
    setSuccess('');
    
    // Validate inputs
    if (!emailSubject.trim()) {
      setError('Prosím zadajte predmet emailu.');
      return;
    }
    
    // Check if content is empty (strip HTML tags and check)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = emailContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    if (!textContent.trim()) {
      setError('Prosím zadajte obsah emailu.');
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare form data for multipart upload
      const formData = new FormData();
      formData.append('subject', emailSubject);
      formData.append('content', emailContent);
      
      if (pdfFile) {
        formData.append('attachment', pdfFile);
      }
      
      const response = await axios.post('communication/admin/communication/send-test', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError('Nepodarilo sa odoslať testovací email.');
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('Nepodarilo sa odoslať testovací email. Skúste prosím znova.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle send
  const handleSend = async () => {
    setError('');
    setSuccess('');
    
    // Validate inputs
    if (!emailSubject.trim()) {
      setError('Prosím zadajte predmet emailu.');
      return;
    }
    
    // Check if content is empty (strip HTML tags and check)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = emailContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    if (!textContent.trim()) {
      setError('Prosím zadajte obsah emailu.');
      return;
    }
    
    if (selectedRecipients.length === 0) {
      setError('Prosím vyberte aspoň jedného príjemcu.');
      return;
    }
    
    // Confirm before sending
    const confirmMessage = `Naozaj chcete odoslať email ${selectedRecipients.length} príjemcom?\n\nKópia emailu bude automaticky odoslaná na timikerekesova@gmail.com a timea.friedrichova@gmail.com pre kontrolu.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare form data for multipart upload
      const formData = new FormData();
      formData.append('subject', emailSubject);
      formData.append('content', emailContent);
      formData.append('recipients', JSON.stringify(selectedRecipients));
      
      if (pdfFile) {
        formData.append('attachment', pdfFile);
      }
      
      const response = await axios.post('communication/admin/communication/send', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        // Background job started successfully
        const totalRecipients = response.data.total_recipients || selectedRecipients.length;
        const monitoringEmails = response.data.monitoring_emails || 2;
        
        setSuccess(
          `Email sa odosiela v pozadí ${totalRecipients} príjemcom. ` +
          `Proces môže trvať niekoľko minút (asi ${Math.ceil(totalRecipients / 5) * 2} sekúnd). ` +
          `Kópia príde na ${monitoringEmails} kontrolné emaily po dokončení.`
        );
        
        // Clear form after successful queue
        setEmailSubject('');
        setEmailContent('');
        setSelectedRecipients([]);
        setPdfFile(null);
        
        // Clear contentEditable editor
        if (editorRef.current) {
          editorRef.current.innerHTML = '';
        }
        
        // Reset file input
        const fileInput = document.getElementById('pdf-attachment');
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setError('Nepodarilo sa zaradiť emaily do fronty.');
      }
    } catch (err) {
      console.error('Error sending emails:', err);
      
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('Nepodarilo sa odoslať emaily. Skúste prosím znova.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Close preview modal
  const closePreview = () => {
    setShowPreview(false);
    setPreviewHtml('');
  };

  return (
    <div className="communication-management">
      <h2>{t('communication management') || 'Správa komunikácie'}</h2>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      {/* Step 1: Fetch Emails */}
      <div className="communication-section">
        <div className="section-header">
          <h3>Krok 1: Získať zoznam klientov</h3>
          <p className="section-description">
            Načítajte zoznam všetkých emailových adries klientov z databázy.
          </p>
        </div>
        
        <div className="action-buttons">
          <button 
            className="fetch-emails-button"
            onClick={() => {
              fetchClientEmails();
              fetchInstructors();
            }}
            disabled={loading || emailsLoaded}
          >
            {loading ? 'Načítavam...' : 'Získať emaily'}
          </button>
          
          {emailsLoaded && (
            <button 
              className="clear-button"
              onClick={() => {
                setEmails([]);
                setEmailsLoaded(false);
                setSelectedRecipients([]);
                setEmailSubject('');
                setEmailContent('');
                setPdfFile(null);
                setShowPreferredFilter(false);
                setSelectedInstructor('');
                setSelectedClass('');
              }}
            >
              Vymazať a začať znova
            </button>
          )}
        </div>
      </div>
      
      {/* Step 1.5: Preferred Students Filter (Optional) */}
      {emailsLoaded && (
        <div className="communication-section preferred-section">
          <div className="section-header">
            <h3>
              Krok 1.5: Nájsť preferovaných študentov (voliteľné) 
              <button 
                className="toggle-filter-button"
                onClick={() => setShowPreferredFilter(!showPreferredFilter)}
                type="button"
              >
                {showPreferredFilter ? '▼ Skryť' : '▶ Zobraziť'}
              </button>
            </h3>
            <p className="section-description">
              Vyhľadaj si študentov / nadšencov, ktorí pravidelne navštevujú špecifickú hodinu s vybraným inštruktorom.
            </p>
          </div>
          
          {showPreferredFilter && (
            <div className="preferred-filter">
              <div className="filter-row">
                <div className="filter-group">
                  <label htmlFor="instructor-select">Inštruktor *</label>
                  <select
                    id="instructor-select"
                    value={selectedInstructor}
                    onChange={handleInstructorChange}
                    className="filter-select"
                  >
                    <option value="">Vyberte inštruktora</option>
                    {instructors.map(instructor => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-group">
                  <label htmlFor="class-select">Trieda *</label>
                  <select
                    id="class-select"
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="filter-select"
                    disabled={!selectedInstructor}
                  >
                    <option value="">Vyberte triedu</option>
                    {availableClasses.map((className, index) => (
                      <option key={index} value={className}>
                        {className}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="filter-row">
                <div className="filter-group">
                  <label htmlFor="time-period">Časové obdobie</label>
                  <select
                    id="time-period"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(e.target.value)}
                    className="filter-select"
                  >
                    <option value="1m">Posledný 1 mesiac</option>
                    <option value="3m">Posledné 3 mesiace</option>
                    <option value="6m">Posledných 6 mesiacov</option>
                    <option value="12m">Posledný rok</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label htmlFor="min-visits">Min. návštev: {minVisits}</label>
                  <input
                    type="range"
                    id="min-visits"
                    min="1"
                    max="10"
                    value={minVisits}
                    onChange={(e) => setMinVisits(parseInt(e.target.value))}
                    className="filter-slider"
                  />
                </div>
              </div>
              
              <div className="filter-actions">
                <button
                  className="find-students-button"
                  onClick={handleFindPreferredStudents}
                  disabled={loading || !selectedInstructor || !selectedClass}
                >
                  {loading ? 'Hľadám...' : 'Nájsť a vybrať študentov'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Step 2: Compose Email */}
      {emailsLoaded && (
        <>
          <div className="communication-section">
            <div className="section-header">
              <h3>Krok 2: Napísať email</h3>
              <p className="section-description">
                Vytvorte obsah emailu, ktorý chcete odoslať.
              </p>
            </div>
            
            <div className="email-composer">
              <div className="form-group">
                <label htmlFor="email-subject">Predmet emailu *</label>
                <input
                  type="text"
                  id="email-subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Zadajte predmet emailu"
                  className="email-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email-content">Obsah emailu *</label>
                <div className="rich-text-editor">
                  <div className="editor-toolbar">
                    <button
                      type="button"
                      onClick={() => formatText('bold')}
                      className="editor-btn"
                      title="Tučné"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText('italic')}
                      className="editor-btn"
                      title="Kurzíva"
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText('underline')}
                      className="editor-btn"
                      title="Podčiarknuté"
                    >
                      <u>U</u>
                    </button>
                    <span className="separator">|</span>
                    <button
                      type="button"
                      onClick={() => formatText('insertUnorderedList')}
                      className="editor-btn"
                      title="Zoznam s odrážkami"
                    >
                      • List
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText('insertOrderedList')}
                      className="editor-btn"
                      title="Číslovaný zoznam"
                    >
                      1. List
                    </button>
                    <span className="separator">|</span>
                    <button
                      type="button"
                      onClick={() => formatText('formatBlock', 'h2')}
                      className="editor-btn"
                      title="Nadpis"
                    >
                      H2
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText('formatBlock', 'h3')}
                      className="editor-btn"
                      title="Podnadpis"
                    >
                      H3
                    </button>
                    <button
                      type="button"
                      onClick={() => formatText('formatBlock', 'p')}
                      className="editor-btn"
                      title="Normálny text"
                    >
                      P
                    </button>
                  </div>
                  <div
                    ref={editorRef}
                    contentEditable
                    className="editor-content"
                    onInput={handleContentChange}
                    data-placeholder="Napíšte obsah vášho emailu..."
                    suppressContentEditableWarning
                    dir="ltr"
                  />
                </div>
              </div>
              <p className="field-hint editor-hint">Automaticky sa pridá logo a päta s kontaktnými údajmi.</p>
              
              <div className="form-group">
                <label htmlFor="pdf-attachment">PDF príloha (voliteľné)</label>
                <input
                  type="file"
                  id="pdf-attachment"
                  accept=".pdf"
                  onChange={handlePdfChange}
                  className="file-input"
                />
                <p className="field-hint">Maximálna veľkosť: 2MB. Použite prosím malé prílohy pod 2MB.</p>
                {pdfFile && (
                  <p className="file-selected">Vybraný súbor: {pdfFile.name} ({(pdfFile.size / 1024).toFixed(2)} KB)</p>
                )}
                {pdfError && <p className="file-error">{pdfError}</p>}
              </div>
            </div>
          </div>
          
          {/* Step 3: Select Recipients */}
          <div className="communication-section">
            <div className="section-header">
              <h3>Krok 3: Vybrať príjemcov</h3>
              <p className="section-description">
                Vyberte klientov, ktorým chcete odoslať email. <strong>BACHA: Emaily sa odošlú VŠETKYM vybraným klientom!</strong>
              </p>
            </div>
            
            <div className="recipients-selection">
              <div className="selection-controls">
                <button 
                  className="select-all-button"
                  onClick={handleSelectAll}
                  type="button"
                >
                  {selectedRecipients.length === emails.length ? 'Zrušiť výber všetkých' : 'Vybrať všetkých'}
                </button>
                <span className="selected-count">
                  Vybraných: <strong>{selectedRecipients.length}</strong> z {emails.length}
                </span>
              </div>
              
              <div className="recipients-list">
                <table className="recipients-table">
                  <thead>
                    <tr>
                      <th style={{width: '50px'}}>
                        <input
                          type="checkbox"
                          checked={selectedRecipients.length === emails.length && emails.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>#</th>
                      <th>Meno</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map((client, index) => (
                      <tr 
                        key={index}
                        className={selectedRecipients.includes(client.email) ? 'selected-row' : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRecipients.includes(client.email)}
                            onChange={() => handleRecipientToggle(client.email)}
                          />
                        </td>
                        <td>{index + 1}</td>
                        <td>{client.name || 'N/A'}</td>
                        <td>{client.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          {/* Step 4: Preview and Send */}
          <div className="communication-section">
            <div className="section-header">
              <h3>Krok 4: Náhľad a odoslanie</h3>
              <p className="section-description">
                Skontrolujte náhľad emailu pred odoslaním.
              </p>
            </div>
            
            <div className="action-buttons">
              <button 
                className="test-button"
                onClick={handleSendTest}
                disabled={loading || !emailSubject.trim() || !emailContent.trim()}
              >
                {loading ? 'Odosielam...' : 'Poslať test na môj email'}
              </button>
              
              <button 
                className="preview-button"
                onClick={handlePreview}
                disabled={loading || !emailSubject.trim() || !emailContent.trim()}
              >
                Zobraziť náhľad
              </button>
              
              <button 
                className="send-button"
                onClick={handleSend}
                disabled={loading || !emailSubject.trim() || !emailContent.trim() || selectedRecipients.length === 0}
              >
                {loading ? 'Odosielam...' : `Odoslať email (${selectedRecipients.length} príjemcov)`}
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Preview Modal */}
      {showPreview && (
        <div className="preview-modal-overlay" onClick={closePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>Náhľad emailu</h3>
              <button className="close-preview-button" onClick={closePreview}>×</button>
            </div>
            <div className="preview-modal-subject">
              <strong>Predmet:</strong> {emailSubject}
            </div>
            <div className="preview-modal-content">
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                style={{width: '100%', height: '500px', border: 'none'}}
              />
            </div>
            <div className="preview-modal-footer">
              <button className="close-button" onClick={closePreview}>
                Zavrieť
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="info-section">
        <p className="info-text">
          <strong>Poznámka:</strong> Táto sekcia je len pre autorizovaných administrátorov. Emaily sa načítajú len z aktívnych, nezablokovaných používateľov.
        </p>
      </div>
    </div>
  );
};

export default CommunicationManagement;
